import { useState, useEffect, useCallback } from 'react';
import { toastService as toast } from '../../../services/toastService';
import { useAuth } from '../../../hooks/useAuth';
import { useLanguage } from '../../../contexts/LanguageContext';
import type { TranslationKeys } from '../../../i18n/fr';
import { billingService, PlanItem } from '../../../services/billingService';
import { authService } from '../../../services/authService';
import { AnimatedIconButton } from '../../common/AnimatedIconButton';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Loader2, Check, X, ArrowLeft, ArrowRight, Eye, EyeOff, CreditCard, KeyRound, Copy, CalendarDays, Gift, Mail, RefreshCw, Crown, Building2, Sparkles } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../ui/tooltip';

type SignupStep = 'account' | 'plan' | 'payment';

interface SignUpFormProps {
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
}

// Helpers
const monthlyEquiv = (annual: number) => Math.round(annual / 12);

// Feature lists per plan
const getPlanFeatures = (planName: string, t: TranslationKeys): string[] => {
  const features: Record<string, string[]> = {
    free: [
      t.auth.featureFree1 ?? '1 utilisateur',
      t.auth.featureFree2 ?? '1 canal de communication',
      t.auth.featureFree3 ?? 'Génération de contenu limitée',
      t.auth.featureFree4 ?? 'Branding RAISO',
      t.auth.featureFree5 ?? 'Fonctionnalités limitées',
    ],
    pro: [
      t.auth.featurePro1 ?? '1 marque / espace',
      t.auth.featurePro2 ?? 'Multi-réseaux sociaux',
      t.auth.featurePro3 ?? 'Veille stratégique illimitée',
      t.auth.featurePro4 ?? 'Génération de contenu illimitée',
      t.auth.featurePro5 ?? 'Calendrier éditorial',
      t.auth.featurePro6 ?? 'Planification & publication',
      t.auth.featurePro7 ?? 'Analytics avancés',
    ],
    agency: [
      t.auth.featureAgency1 ?? '10 sous-comptes clients',
      t.auth.featureAgency2 ?? 'Multi-marques',
      t.auth.featureAgency3 ?? 'Dashboard agence centralisé',
      t.auth.featureAgency4 ?? 'Gestion des accès',
      t.auth.featureAgency5 ?? 'Analytics par client',
      t.auth.featureAgency6 ?? 'Planification multi-comptes',
    ],
  };
  return features[planName] || features.free;
};

const getPlanTagline = (planName: string, t: TranslationKeys): string => {
  const taglines: Record<string, string> = {
    free: t.auth.taglineFree ?? 'Découvrez RAISO gratuitement',
    pro: t.auth.taglinePro ?? 'Tout ce qu\'il faut pour un créateur solo',
    agency: t.auth.taglineAgency ?? 'Gérez tous vos clients depuis un seul espace',
  };
  return taglines[planName] || taglines.free;
};

const getPlanCTA = (planName: string, t: TranslationKeys): string => {
  const ctas: Record<string, string> = {
    free: t.auth.ctaFree ?? 'Commencer gratuitement',
    pro: t.auth.ctaPro ?? 'Commencer Pro',
    agency: t.auth.ctaAgency ?? 'Commencer Agency',
  };
  return ctas[planName] || ctas.free;
};

export function SignUpForm({ email, setEmail, password, setPassword }: SignUpFormProps) {
  const { registerWithPlan } = useAuth();
  const { t, language } = useLanguage();

  const [step, setStep] = useState<SignupStep>('account');
  const [fullName, setFullName] = useState('');
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('annual');
  const [plansLoading, setPlansLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [emailVerificationPending, setEmailVerificationPending] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  // Invitation token from URL
  const [invitationToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('invitation') || '';
  });
  const [invitationInfo] = useState<{ email?: string; agencyName?: string } | null>(() => {
    if (!invitationToken) return null;
    try {
      const payload = JSON.parse(atob(invitationToken.split('.')[1]));
      return { email: payload.client_email, agencyName: payload.agency_name };
    } catch {
      return null;
    }
  });

  // Pre-fill email from invitation
  useEffect(() => {
    if (invitationInfo?.email && !email) {
      setEmail(invitationInfo.email);
    }
  }, [invitationInfo]);

  const passwordRules = [
    { label: t.auth.passwordRuleMinLength, test: (p: string) => p.length >= 8 },
    { label: t.auth.passwordRuleUppercase, test: (p: string) => /[A-Z]/.test(p) },
    { label: t.auth.passwordRuleDigit, test: (p: string) => /[0-9]/.test(p) },
    { label: t.auth.passwordRuleSpecial, test: (p: string) => /[^A-Za-z0-9]/.test(p) },
  ];
  const allRulesPassed = passwordRules.every(r => r.test(password));

  const handleGenerate = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+';
    let newPassword = '';
    newPassword += 'A';
    newPassword += '1';
    newPassword += '!';
    for (let i = 3; i < 16; i++) {
        newPassword += chars[Math.floor(Math.random() * chars.length)];
    }
    newPassword = newPassword.split('').sort(() => 0.5 - Math.random()).join('');
    setPassword(newPassword);
    setConfirmPassword(newPassword);
    toast.warning(t.auth.passwordSaveReminder ?? '⚠️ Notez votre mot de passe quelque part avant de passer à l\'étape suivante !');
  };

  const handleCopy = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(t.auth.passwordCopied ?? 'Password copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const loadPlans = useCallback(async () => {
    if (plans.length > 0) return;
    setPlansLoading(true);
    try {
      const result = await billingService.getPlans();
      setPlans(result);
    } catch {
      toast.error(t.auth.loadPlansError);
    } finally {
      setPlansLoading(false);
    }
  }, [plans.length]);

  useEffect(() => {
    if (step === 'plan') loadPlans();
  }, [step, loadPlans]);

  // Auto-select Pro plan (annual by default)
  useEffect(() => {
    if (plans.length > 0 && !selectedPlanId) {
      const pro = plans.find(p => p.name === 'pro');
      if (pro) setSelectedPlanId(pro.id);
    }
  }, [plans]);

  const handleNextStep = async () => {
    if (step === 'account') {
      if (!allRulesPassed) {
        setError(t.auth.passwordSecurityError);
        return;
      }
      if (password !== confirmPassword) {
        setError(t.auth.passwordsMismatch);
        return;
      }
      setEmailChecking(true);
      try {
        const exists = await authService.checkEmail(email);
        if (exists) {
          setError(t.auth.emailAlreadyExists);
          return;
        }
      } catch {
        // If check fails, let registration handle it
      } finally {
        setEmailChecking(false);
      }
      setError('');
      setStep('plan');
    } else if (step === 'plan' && selectedPlanId) {
      setError('');
      setStep('payment');
    }
  };

  const handleBackStep = () => {
    setError('');
    if (step === 'payment') setStep('plan');
    else if (step === 'plan') setStep('account');
  };

  const handleSignup = async () => {
    if (!selectedPlanId) return;
    setIsLoading(true);
    setError('');
    try {
      const result = await registerWithPlan({
        email,
        password,
        full_name: fullName || undefined,
        plan_id: selectedPlanId,
        billing_interval: billingInterval,
        referral_code: referralCode.trim() || undefined,
        invitation_token: invitationToken || undefined,
        language,
      });
      if (result.requiresEmailVerification) {
        setEmailVerificationPending(true);
      } else if (result.checkoutUrl) {
        toast.success(t.auth.redirectingToStripe);
        window.location.href = result.checkoutUrl;
      } else {
        toast.success(t.auth.signupSuccess);
      }
    } catch {
      setError(t.auth.signupError);
      toast.error(t.auth.signupError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      await authService.resendVerificationEmail(email);
      toast.success(t.auth.verificationEmailResent ?? 'Email de vérification renvoyé !');
    } catch {
      toast.error(t.auth.verificationEmailResendError ?? 'Erreur lors de l\'envoi');
    } finally {
      setResendingEmail(false);
    }
  };

  const selectedPlan = plans.find(p => p.id === selectedPlanId);
  const hasAnnualOption = (plan: PlanItem) => plan.annual_subscription_price != null && plan.annual_subscription_price > 0;
  const anyPlanHasAnnual = plans.some(hasAnnualOption);
  const getDisplayPrice = (plan: PlanItem) => billingInterval === 'annual' && hasAnnualOption(plan) ? plan.annual_subscription_price! : plan.monthly_subscription_price;
  const getDisplaySuffix = (plan: PlanItem) => billingInterval === 'annual' && hasAnnualOption(plan) ? (t.auth.perYear ?? '/an') : (t.auth.perMonth ?? '/mois');
  const getAnnualSaving = (plan: PlanItem) => {
    if (!hasAnnualOption(plan)) return 0;
    const monthlyTotal = plan.monthly_subscription_price * 12;
    return Math.round(((monthlyTotal - plan.annual_subscription_price!) / monthlyTotal) * 100);
  };
  const selectedPlanPriceCentsHT = selectedPlan ? Math.round(getDisplayPrice(selectedPlan) * 100) : 0;

  // Step indicator
  const allSteps: SignupStep[] = ['account', 'plan', 'payment'];
  const stepLabels: Record<SignupStep, string> = {
    account: t.auth.stepAccount,
    plan: t.auth.stepPlan,
    payment: t.auth.stepPayment,
  };

  // ── Email verification pending screen ──
  if (emailVerificationPending) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-6 text-center">
        <div className="auth-accent-soft w-16 h-16 rounded-full flex items-center justify-center">
          <Mail className="auth-accent w-8 h-8" />
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">
            {t.auth.verifyEmailTitle ?? 'Vérifiez votre email'}
          </h2>
          <p className="text-sm text-[var(--color-text-secondary)] max-w-sm">
            {(t.auth.verifyEmailDescription ?? 'Un email de vérification a été envoyé à {email}. Cliquez sur le lien pour activer votre compte.').replace('{email}', email)}
          </p>
        </div>
        <div className="px-4 py-3 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-primary)]">
          {email}
        </div>
        <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-400 text-left max-w-sm">
          <span className="mt-0.5 shrink-0">⚠️</span>
          <span>{t.auth.checkSpamFolder ?? "Si vous ne trouvez pas l'email, pensez à vérifier votre dossier spam ou courriers indésirables."}</span>
        </div>
        <button
          type="button"
          onClick={handleResendVerification}
          disabled={resendingEmail}
          className="flex items-center gap-2 text-sm font-medium auth-accent disabled:opacity-50 transition-colors"
        >
          {resendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {t.auth.resendVerificationEmail ?? 'Renvoyer l\'email de vérification'}
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {allSteps.map((s, i) => {
          const isCurrent = step === s;
          const isDone = allSteps.indexOf(step) > i;
          return (
            <div key={s} className="flex items-center gap-2">
              <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition-colors ${
                isCurrent ? 'auth-selected' : isDone ? 'bg-green-500 text-white' : 'bg-[var(--color-surface)] text-[var(--color-text-secondary)] border border-[var(--color-border)]'
              }`}>
                {isDone ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-xs hidden sm:inline ${isCurrent ? 'text-[var(--color-text-primary)] font-medium' : 'text-[var(--color-text-secondary)]'}`}>
                {stepLabels[s]}
              </span>
              {i < allSteps.length - 1 && <div className="w-6 h-px bg-[var(--color-border)]" />}
            </div>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-sm text-red-600 font-medium border border-red-100 flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <div className="w-1.5 h-1.5 rounded-full bg-red-600" />
          {error}
        </div>
      )}

      {/* ── Step 1: Account ── */}
      {step === 'account' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName" className="text-[var(--color-text-primary)]">{t.auth.fullNameLabel}</Label>
            <Input id="fullName" type="text" placeholder={t.auth.fullNamePlaceholder} value={fullName} onChange={e => setFullName(e.target.value)} className="px-4 bg-[var(--color-surface)] border-[var(--color-border)] focus:ring-[var(--color-primary)] transition-all" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="signup-email" className="text-[var(--color-text-primary)]">{t.auth.emailLabel}</Label>
            <Input id="signup-email" data-testid="signup-email" type="email" placeholder={t.auth.emailPlaceholder || 'name@example.com'} value={email} onChange={e => setEmail(e.target.value)} required className="px-4 bg-[var(--color-surface)] border-[var(--color-border)] focus:ring-[var(--color-primary)] transition-all" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="signup-password" className="text-[var(--color-text-primary)]">{t.auth.passwordLabel}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input id="signup-password" data-testid="signup-password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required className="px-4 pr-14 bg-[var(--color-surface)] border-[var(--color-border)] focus:ring-[var(--color-primary)] transition-all" />
                <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-secondary)' }}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <AnimatedIconButton icon={KeyRound} onClick={handleGenerate} variant="secondary" size="default" revealLabelOnHover />
              <AnimatedIconButton icon={copied ? Check : Copy} onClick={handleCopy} variant={copied ? 'success' : 'neutral'} size="default" revealLabelOnHover disabled={!password} />
            </div>
            {password.length > 0 && (
              <ul className="space-y-1 pt-1">
                {passwordRules.map(rule => {
                  const passed = rule.test(password);
                  return (
                    <li key={rule.label} className={`flex items-center gap-2 text-xs transition-colors ${passed ? 'text-green-600' : 'text-red-500'}`}>
                      <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 ${passed ? 'bg-green-500' : 'bg-red-500'}`}>
                        {passed ? <Check className="w-2.5 h-2.5 text-white" /> : <X className="w-2.5 h-2.5 text-white" />}
                      </div>
                      {rule.label}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="signup-confirm-password" className="text-[var(--color-text-primary)]">{t.auth.confirmPasswordLabel}</Label>
            <div className="relative">
              <Input id="signup-confirm-password" type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className={`px-4 pr-14 bg-[var(--color-surface)] border-[var(--color-border)] focus:ring-[var(--color-primary)] transition-all ${confirmPassword.length > 0 && confirmPassword !== password ? 'border-red-400' : ''}`} />
              <button type="button" onClick={() => setShowConfirmPassword(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-secondary)' }}>
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {confirmPassword.length > 0 && confirmPassword !== password && (
              <p className="text-xs text-red-500">{t.auth.passwordsMismatch}</p>
            )}
            {confirmPassword.length > 0 && confirmPassword === password && (
              <p className="text-xs text-green-600 flex items-center gap-1"><Check className="w-3 h-3" /> {t.auth.passwordsMatch}</p>
            )}
          </div>
          <div className="flex justify-end pt-2">
            {/* V4-SIGNUP-WIZARD-DOUBLE-CLICK (2026-07-03, root cause réel) — tous les
                boutons de progression du wizard (Suivant/Retour/Créer) utilisaient
                revealLabelOnHover=true : ActionButtonBase applique `hover:w-auto` +
                `transition-all duration-300`, donc le bouton change de largeur (icône
                seule → icône+label) PENDANT les ~300ms qui suivent le survol. Un clic
                qui arrive avant la fin de cette transition atterrit hors de la zone
                (encore en cours d'agrandissement) et retombe sur le <div> conteneur
                (`flex justify-end`), qui n'a pas de onClick → clic silencieusement
                perdu. Repro confirmée via listener capture-phase : e.target = DIV,
                pas le bouton. Fix : revealLabelOnHover={false} sur ces CTA (label
                statique, pas de resize au survol → plus de hitbox mobile). */}
            {(() => {
              const isDisabled = !email || !allRulesPassed || password !== confirmPassword || emailChecking;
              const disabledReason = emailChecking
                ? 'Vérification de l\'email en cours...'
                : !email
                ? 'Veuillez saisir votre adresse email'
                : !allRulesPassed
                ? 'Le mot de passe ne respecte pas toutes les règles'
                : password !== confirmPassword
                ? 'Les mots de passe ne correspondent pas'
                : '';
              return isDisabled ? (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <AnimatedIconButton
                          icon={ArrowRight}
                          label={t.auth.nextButton}
                          testId="signup-next"
                          onClick={handleNextStep}
                          disabled
                          loading={emailChecking}
                          variant="default"
                          size="lg"
                          revealLabelOnHover={false}
                        />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="left">{disabledReason}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <AnimatedIconButton
                  icon={ArrowRight}
                  label={t.auth.nextButton}
                  testId="signup-next"
                  onClick={handleNextStep}
                  disabled={false}
                  loading={emailChecking}
                  variant="default"
                  size="lg"
                  revealLabelOnHover={false}
                />
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Step 2: Plan selection — 3-column grid ── */}
      {step === 'plan' && (
        <div className="space-y-4">
          <AnimatedIconButton
            icon={ArrowLeft}
            label={t.auth.backButton}
            onClick={handleBackStep}
            variant="neutral"
            size="default"
            revealLabelOnHover={false}
          />

          {/* Billing interval toggle */}
          {!plansLoading && anyPlanHasAnnual && (
            <div className="flex items-center justify-center gap-1 p-1 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)]" data-testid="signup-interval-toggle">
              <button
                type="button"
                onClick={() => setBillingInterval('monthly')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer ${
                  billingInterval === 'monthly'
                    ? 'auth-selected shadow-sm'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                <CalendarDays className="w-3.5 h-3.5" />
                {t.auth.billingMonthly}
              </button>
              <button
                type="button"
                onClick={() => setBillingInterval('annual')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer ${
                  billingInterval === 'annual'
                    ? 'auth-selected shadow-sm'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                <CalendarDays className="w-3.5 h-3.5" />
                {t.auth.billingAnnual}
                {billingInterval === 'annual' && (
                  <span className="ml-0.5 text-xs bg-green-500 text-white rounded px-1.5 py-0.5 font-semibold">-20%</span>
                )}
                {billingInterval !== 'annual' && (
                  <span className="ml-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded px-1.5 py-0.5 font-semibold">-20%</span>
                )}
              </button>
            </div>
          )}

          {plansLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin auth-accent" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {plans.filter(plan => plan.name !== 'enterprise').map(plan => {
                const isPro = plan.name === 'pro';
                const isSelected = selectedPlanId === plan.id;
                const price = getDisplayPrice(plan);
                const isAnnual = billingInterval === 'annual' && hasAnnualOption(plan);
                const saving = getAnnualSaving(plan);
                const features = getPlanFeatures(plan.name, t);
                const tagline = getPlanTagline(plan.name, t);
                const cta = getPlanCTA(plan.name, t);
                const PlanIcon = plan.is_agency_plan ? Building2 : isPro ? Crown : Sparkles;

                return (
                  <button
                    key={plan.id}
                    type="button"
                    data-testid={`signup-plan-${plan.id}`}
                    onClick={() => setSelectedPlanId(plan.id)}
                    className={`relative flex flex-col text-left p-5 rounded-xl border-2 transition-all cursor-pointer duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                      isSelected
                        ? 'border-indigo-500 bg-[var(--color-surface)] shadow-lg'
                        : isPro
                          ? 'auth-accent-border bg-[var(--color-surface)] shadow-md hover:shadow-lg'
                          : 'border-[var(--color-border)] bg-[var(--color-surface)] shadow-sm hover:shadow-md hover:border-[var(--color-primary)]'
                    }`}
                    style={isSelected ? { boxShadow: '0 0 0 2px rgba(99,102,241,0.4), 0 0 25px rgba(99,102,241,0.35), 0 0 50px rgba(99,102,241,0.15)' } : undefined}
                  >
                    {/* Most popular badge */}
                    {isPro && (
                      <div className="absolute top-1 right-1 px-3 py-1 rounded-full auth-selected text-[10px] font-semibold whitespace-nowrap shadow-md z-10">
                        {t.auth.mostPopular ?? 'LE PLUS POPULAIRE'}
                      </div>
                    )}

                    {/* Plan icon + name */}
                    <div className="flex items-center gap-2 mb-2 pr-36 pt-8">
                      <div className={`rounded-lg p-1.5 flex-shrink-0 ${isPro ? 'auth-accent-soft' : 'bg-[var(--color-border)]/40'}`}>
                        <PlanIcon className={`w-4 h-4 ${isPro ? 'auth-accent' : 'text-[var(--color-text-secondary)]'}`} />
                      </div>
                      <h3 className="text-lg font-bold text-[var(--color-text-primary)]">{plan.display_name}</h3>
                    </div>

                    {/* Tagline */}
                    <p className="text-xs text-[var(--color-text-secondary)] mb-4">{tagline}</p>

                    {/* Price */}
                    <div className="mb-4">
                      {price > 0 ? (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-bold text-[var(--color-text-primary)]">
                              {price.toLocaleString('fr-FR')}€
                            </span>
                            <span className={`text-sm ${isPro ? 'auth-accent' : 'text-[var(--color-text-secondary)]'}`}>
                              {t.auth.htLabel ?? 'HT'} {getDisplaySuffix(plan)}
                            </span>
                          </div>
                          <p className={`text-xs mt-0.5 ${isPro ? 'auth-accent' : 'text-[var(--color-text-secondary)]'}`}>
                            {t.auth.vatAtCheckout ?? 'TVA calculée au paiement'}
                          </p>
                          {isAnnual && (
                            <p className={`text-xs mt-0.5 ${isPro ? 'auth-accent' : 'text-[var(--color-text-secondary)]'}`}>
                              {monthlyEquiv(price)}€/{t.auth.monthShort ?? 'mois'} {t.auth.billedAnnually ?? 'facturé annuellement'}
                            </p>
                          )}
                          {isAnnual && saving > 0 && (
                            <span className="inline-block mt-1 text-xs font-medium text-green-600 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded">
                              {t.auth.annualSavePercent.replace('{percent}', String(saving))}
                            </span>
                          )}
                        </>
                      ) : (
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-[var(--color-text-primary)]">
                            {t.auth.freePrice ?? 'Gratuit'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Feature checklist */}
                    <ul className="space-y-2 mb-4 flex-1">
                      {features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs text-[var(--color-text-secondary)]">
                          <Check className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isPro ? 'auth-accent' : 'text-green-500'}`} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Agency extra seat badge */}
                    {plan.is_agency_plan && plan.agency_extra_seat_price_cents != null && plan.agency_extra_seat_price_cents > 0 && (
                      <div className="mb-3 px-3 py-1.5 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs font-medium text-amber-700 dark:text-amber-400 text-center">
                        +{(plan.agency_extra_seat_price_cents / 100).toLocaleString('fr-FR')}€ {t.auth.perExtraClient ?? '/ client supplémentaire'}
                      </div>
                    )}

                    {/* CTA */}
                    <div className={`w-full text-center py-3.5 rounded-xl text-sm font-semibold transition-all ${
                      isSelected
                        ? 'auth-selected shadow-md'
                        : isPro
                          ? 'auth-accent-soft'
                          : 'bg-[var(--color-surface)] text-[var(--color-text-primary)] border border-[var(--color-border)] hover:border-[var(--color-primary)]'
                    }`}>
                      {isSelected
                        ? <span className="flex items-center justify-center gap-1.5 text-white"><Check className="w-4 h-4 text-white" /> {t.auth.selectedPlan}</span>
                        : cta}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <AnimatedIconButton
              icon={ArrowRight}
              label={t.auth.nextButton}
              testId="signup-next"
              onClick={handleNextStep}
              disabled={!selectedPlanId}
              variant="default"
              size="lg"
              revealLabelOnHover={false}
            />
          </div>
        </div>
      )}

      {/* ── Step 3: Payment / Promo ── */}
      {step === 'payment' && (
        <div className="space-y-6">
          <AnimatedIconButton
            icon={ArrowLeft}
            label={t.auth.backButton}
            onClick={handleBackStep}
            variant="neutral"
            size="default"
            revealLabelOnHover={false}
          />

          {/* Plan summary */}
          {selectedPlan && (
            <div className="p-5 rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)] space-y-1">
              <p className="text-xs font-medium uppercase tracking-wider text-[var(--color-text-secondary)]">{t.auth.selectedPlan}</p>
              <p className="text-lg font-semibold text-[var(--color-text-primary)]">
                {selectedPlan.display_name} — {getDisplayPrice(selectedPlan) > 0
                  ? `${getDisplayPrice(selectedPlan).toLocaleString('fr-FR')}€ ${t.auth.htLabel ?? 'HT'}${getDisplaySuffix(selectedPlan)}`
                  : (t.auth.freePrice ?? 'Gratuit')
                }
              </p>
            </div>
          )}

          {/* Referral code */}
          <div className="space-y-3">
            <Label className="text-[var(--color-text-primary)] flex items-center gap-2">
              <Gift className="w-4 h-4" /> {t.auth.referralCodeLabel ?? 'Code de parrainage (optionnel)'}
            </Label>
            <Input
              type="text"
              data-testid="signup-referral"
              placeholder={t.auth.referralCodePlaceholder ?? 'Code de parrainage'}
              value={referralCode}
              onChange={e => setReferralCode(e.target.value.toUpperCase())}
              className="px-4 bg-[var(--color-surface)] border-[var(--color-border)] focus:ring-[var(--color-primary)] transition-all uppercase"
            />
          </div>

          {/* Price breakdown */}
          {selectedPlan && (
            <div className="p-5 rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)] space-y-3 text-sm">
              <div className="flex justify-between font-semibold text-base">
                <span className="text-[var(--color-text-primary)]">{t.auth.totalToPay} ({t.auth.htLabel ?? 'HT'})</span>
                <span className="text-[var(--color-text-primary)]">{(selectedPlanPriceCentsHT / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€</span>
              </div>
              <p className="text-xs text-center text-[var(--color-text-secondary)]">{t.auth.vatAtCheckout ?? 'TVA calculée au paiement'}</p>
              {selectedPlanPriceCentsHT === 0 && (
                <p className="text-green-600 font-medium text-center pt-1">{t.auth.freeAccess}</p>
              )}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <AnimatedIconButton
              icon={CreditCard}
              label={isLoading ? t.auth.redirectingToStripe : selectedPlanPriceCentsHT > 0 ? t.auth.createAccountAndPay : t.auth.createAccountButton}
              onClick={handleSignup}
              disabled={isLoading}
              loading={isLoading}
              variant="default"
              size="lg"
              revealLabelOnHover={false}
              testId="signup-submit"
            />
          </div>
        </div>
      )}
    </>
  );
}
